import { useCallback, useEffect, useRef, useState } from 'react';
import { arrowHead } from '../game/model/arrow';
import type { BoardData } from '../game/model/board';
import type { Direction } from '../game/model/direction';
import { BoardInput } from '../game/rendering/boardInput';
import { BoardScene, WebGLUnavailableError } from '../game/rendering/boardScene';
import { registerTestBoardScene } from '../app/testHooks';
import type { RenderTheme } from '../game/rendering/theme';
import type { MoveResult } from '../game/rules/gameSession';

/**
 * Hosts the PixiJS board inside React.
 *
 * React owns mounting, theming and accessibility; PixiJS owns everything that changes per
 * frame. Taps travel React -> store -> back here as a `MoveResult`, and only the resulting
 * animation touches the renderer, so no arrow movement ever triggers a re-render.
 */

export interface BoardViewProps {
  readonly board: BoardData;
  readonly theme: RenderTheme;
  readonly reducedMotion: boolean;
  readonly showBlockerHint: boolean;
  readonly hintArrowId: number;
  readonly focusArrowId: number;
  /** Arrows still on the board, ascending. Supplied by the session, not read back from the scene. */
  readonly aliveArrowIds: readonly number[];
  readonly interactive: boolean;
  /** Incremented by the HUD's fit control; any change refits the camera. */
  readonly fitSignal: number;
  /**
   * Changes whenever the board must be re-applied from the session — a new level, a resumed
   * one, or a restart. A restart hands back the same board object with every arrow restored,
   * so identity alone cannot signal it.
   */
  readonly boardEpoch: number;
  onActivate(arrowId: number): MoveResult;
  onFocusArrow(arrowId: number): void;
  onFirstInteraction?(): void;
  /** Called once the winning arrow's exit animation has finished, to reveal the win. */
  onWinSettled?(): void;
}

interface FocusRect {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

export function BoardView(props: BoardViewProps) {
  const {
    board,
    theme,
    reducedMotion,
    showBlockerHint,
    hintArrowId,
    focusArrowId,
    aliveArrowIds,
    interactive,
    fitSignal,
    boardEpoch,
    onActivate,
    onFocusArrow,
    onFirstInteraction,
    onWinSettled,
  } = props;

  const hostRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<BoardScene | null>(null);
  const inputRef = useRef<BoardInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusRect, setFocusRect] = useState<FocusRect | null>(null);
  const [announcement, setAnnouncement] = useState('');
  /**
   * Whether the focus indicator should be shown, following `:focus-visible` semantics: it
   * appears for keyboard use and stays out of the way during pointer play, where it would
   * just be a rectangle sitting on the board for no reason the player can see.
   */
  const [keyboardFocus, setKeyboardFocus] = useState(false);

  /**
   * The scene and its pointer handlers are created once and live outside React, so they read
   * the current props from a ref. The ref is refreshed in an effect rather than during
   * render: pointer and keyboard handlers only ever run after commit, so they always observe
   * the values that were painted.
   */
  const latest = useRef({
    board,
    aliveArrowIds,
    focusArrowId,
    showBlockerHint,
    onActivate,
    onFocusArrow,
    onFirstInteraction,
    onWinSettled,
    interactive,
  });

  useEffect(() => {
    latest.current = {
      board,
      aliveArrowIds,
      focusArrowId,
      showBlockerHint,
      onActivate,
      onFocusArrow,
      onFirstInteraction,
      onWinSettled,
      interactive,
    };
  });

  const handleActivate = useCallback((arrowId: number) => {
    const current = latest.current;
    if (!current.interactive) return;
    current.onFirstInteraction?.();

    const scene = sceneRef.current;
    const result = current.onActivate(arrowId);

    // The winning move only resolves once its arrow has slid off the board. With no scene to
    // animate it, resolve immediately so the win can never hang.
    const onExit = result.kind === 'removed' && result.won ? current.onWinSettled : undefined;
    if (scene === null) {
      onExit?.();
      return;
    }

    if (result.kind === 'removed') {
      scene.removeArrow(arrowId, onExit);
      setAnnouncement(`Arrow removed. ${result.remaining} remaining.`);
      // Focus must not linger on an arrow that has left the board, or the next Enter press
      // would do nothing and keyboard players would be stranded.
      if (current.focusArrowId === arrowId) {
        const remaining = current.aliveArrowIds.filter((id) => id !== arrowId);
        current.onFocusArrow(nearestArrow(current.board, remaining, arrowId));
      }
    } else if (result.kind === 'blocked') {
      scene.rejectArrow(arrowId, result.blockerId, current.showBlockerHint);
      setAnnouncement(
        `Blocked. ${result.heartsRemaining} ${result.heartsRemaining === 1 ? 'heart' : 'hearts'} left.`,
      );
    }
  }, []);

  const updateFocusRect = useCallback((arrowId: number) => {
    const scene = sceneRef.current;
    if (scene === null || arrowId < 0) {
      setFocusRect(null);
      return;
    }
    const position = scene.cellScreenPosition(arrowId);
    setFocusRect(position === null ? null : position);
  }, []);

  // Scene lifecycle: created once for the lifetime of the view.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    let cancelled = false;

    void BoardScene.create(host, {
      theme,
      reducedMotion,
      onContextLost: () => setError('The graphics context was lost. Reload to continue.'),
    })
      .then((scene) => {
        if (cancelled) {
          scene.destroy();
          return;
        }
        sceneRef.current = scene;
        inputRef.current = new BoardInput(scene, {
          onActivate: handleActivate,
          onPress: (arrowId) => scene.pressArrow(arrowId),
          onCameraChange: () => updateFocusRect(latest.current.focusArrowId),
        });
        // Seed the scene from the session, not from the board: a resumed level starts with
        // some arrows already gone.
        scene.setBoard(board, latest.current.aliveArrowIds);
        registerTestBoardScene(scene);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof WebGLUnavailableError
            ? 'This browser could not start WebGL, which the board needs to draw.'
            : `The board could not be created: ${String(cause)}`,
        );
      });

    return () => {
      cancelled = true;
      registerTestBoardScene(undefined);
      inputRef.current?.dispose();
      inputRef.current = null;
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once; updates below.
  }, []);

  useEffect(() => {
    // Re-apply the whole board whenever the session says it changed shape. The alive set
    // comes from the session, so the renderer can never disagree with the rules about which
    // arrows exist — the failure that let removed arrows keep blocking after a restart.
    sceneRef.current?.setBoard(board, latest.current.aliveArrowIds);
  }, [board, boardEpoch]);

  useEffect(() => {
    if (fitSignal === 0) return;
    sceneRef.current?.fit();
    updateFocusRect(latest.current.focusArrowId);
  }, [fitSignal, updateFocusRect]);

  useEffect(() => {
    sceneRef.current?.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    sceneRef.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion]);

  useEffect(() => {
    sceneRef.current?.setHint(hintArrowId);
  }, [hintArrowId]);

  useEffect(() => {
    const visible = keyboardFocus ? focusArrowId : -1;
    sceneRef.current?.setFocus(visible);
    updateFocusRect(visible);
  }, [focusArrowId, keyboardFocus, updateFocusRect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const scene = sceneRef.current;
      if (scene === null) return;

      // Any key press means the player is on the keyboard, so show the indicator and make
      // sure there is something for Enter to act on, however focus arrived here.
      setKeyboardFocus(true);
      let current = focusArrowId;
      if (current < 0 && aliveArrowIds.length > 0) {
        current = aliveArrowIds[0];
        onFocusArrow(current);
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (current >= 0) handleActivate(current);
        return;
      }
      if (event.key === 'f' || event.key === 'F') {
        event.preventDefault();
        scene.fit();
        updateFocusRect(current);
        return;
      }

      const direction = keyToDirection(event.key);
      if (direction === null) return;
      event.preventDefault();

      const next = findNeighbourArrow(board, aliveArrowIds, current, direction);
      if (next >= 0) {
        onFocusArrow(next);
        const arrow = board.arrows[next];
        const head = arrowHead(arrow);
        setAnnouncement(
          `Arrow ${next + 1}, ${arrow.cells.length} ${arrow.cells.length === 1 ? 'cell' : 'cells'} long, ` +
            `pointing ${arrow.direction}, head at column ${head.x + 1}, row ${head.y + 1}.`,
        );
      }
    },
    [board, aliveArrowIds, focusArrowId, handleActivate, onFocusArrow, updateFocusRect],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Only a keyboard arrival (Tab) selects an arrow and reveals the indicator; clicking
      // the board also focuses it, and there the pointer already says what is selected.
      if (!event.currentTarget.matches(':focus-visible')) return;
      setKeyboardFocus(true);
      if (focusArrowId < 0 && aliveArrowIds.length > 0) onFocusArrow(aliveArrowIds[0]);
    },
    [aliveArrowIds, focusArrowId, onFocusArrow],
  );

  const handleBlur = useCallback(() => setKeyboardFocus(false), []);
  const handlePointerDown = useCallback(() => setKeyboardFocus(false), []);

  if (error !== null) {
    return (
      <div className="ar-board">
        <div className="ar-board__status" role="alert">
          <p className="ar-subtitle">Board unavailable</p>
          <p className="ar-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="ar-board"
      ref={hostRef}
      tabIndex={0}
      role="application"
      aria-roledescription="puzzle board"
      aria-label={boardSummary(board)}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPointerDown={handlePointerDown}
    >
      {focusRect !== null && keyboardFocus && (
        <div
          className="ar-board__focus-ring"
          style={{
            left: `${focusRect.x}px`,
            top: `${focusRect.y}px`,
            width: `${focusRect.size}px`,
            height: `${focusRect.size}px`,
          }}
        />
      )}
      <p className="ar-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}

function boardSummary(board: BoardData): string {
  return `Puzzle board, ${board.width} columns by ${board.height} rows, ${board.arrows.length} arrows. Use the arrow keys to move between arrows and Enter to send one off the board.`;
}

function keyToDirection(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowRight':
      return 'right';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    default:
      return null;
  }
}

/** Closest remaining arrow to a reference arrow, by Manhattan distance then by id. */
export function nearestArrow(
  board: BoardData,
  candidateIds: readonly number[],
  referenceId: number,
): number {
  const reference = board.arrows[referenceId];
  if (reference === undefined || candidateIds.length === 0) return candidateIds[0] ?? -1;
  const from = arrowHead(reference);

  let bestId = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const id of candidateIds) {
    const head = arrowHead(board.arrows[id]);
    const distance = Math.abs(head.x - from.x) + Math.abs(head.y - from.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * Spatial keyboard navigation: prefer arrows in the same row or column in the pressed
 * direction, then fall back to the nearest arrow in that half-plane.
 */
export function findNeighbourArrow(
  board: BoardData,
  candidateIds: readonly number[],
  currentId: number,
  direction: Direction,
): number {
  if (candidateIds.length === 0) return -1;
  if (currentId < 0) return candidateIds[0];
  const current = board.arrows[currentId];
  if (current === undefined) return candidateIds[0];
  const from = arrowHead(current);

  let bestId = -1;
  let bestScore = Number.POSITIVE_INFINITY;

  // Navigation compares head cells: that is the end the player acts on.
  for (const id of candidateIds) {
    if (id === currentId) continue;
    const head = arrowHead(board.arrows[id]);
    const dx = head.x - from.x;
    const dy = head.y - from.y;

    let along = 0;
    let across = 0;
    switch (direction) {
      case 'up':
        along = -dy;
        across = Math.abs(dx);
        break;
      case 'down':
        along = dy;
        across = Math.abs(dx);
        break;
      case 'left':
        along = -dx;
        across = Math.abs(dy);
        break;
      case 'right':
        along = dx;
        across = Math.abs(dy);
        break;
    }
    if (along <= 0) continue;

    // Cross-axis drift is weighted heavily so movement feels like a straight line.
    const score = along + across * 4;
    if (score < bestScore) {
      bestScore = score;
      bestId = id;
    }
  }
  return bestId;
}
